import * as React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface DecileRange {
  from: number;
  to: number;
}

interface DecileBenchmarkBarProps {
  value: number;
  deciles: DecileRange[];
  inverse?: boolean;
  variant?: "detailed" | "compact" | "segmented" | "modal" | "muted";
  styleVariant?: "stealth" | "graphite" | "teal";
  algorithm?: string;
  cmsAvg?: number;
}

function formatPct(n: number) {
  return `${n.toFixed(2)}%`;
}

const getDecileColor = (
  index: number,
  isActive: boolean,
  inverse: boolean,
  styleVariant: "stealth" | "graphite" | "teal" = "stealth"
) => {
  const baseColors = [
    "from-red-700 to-red-600",
    "from-red-600 to-red-500",
    "from-amber-700 to-amber-600",
    "from-amber-600 to-amber-500",
    "from-amber-500 to-amber-400",
    "from-amber-500 to-amber-400",
    "from-emerald-700 to-emerald-600",
    "from-emerald-600 to-emerald-500",
    "from-emerald-600 to-emerald-500",
    "from-emerald-500 to-emerald-400"
  ];
  const colors = inverse ? [...baseColors].reverse() : baseColors;
  if (isActive) return `bg-gradient-to-r ${colors[index]} shadow-lg transform scale-110 z-10`;
  return `bg-gradient-to-r ${colors[index]} opacity-80 hover:opacity-90`;
};

export default function DecileBenchmarkBar({ value, deciles, inverse, variant = "detailed", styleVariant = "stealth", algorithm, cmsAvg }: DecileBenchmarkBarProps) {
  const clamped = Math.max(0, Math.min(100, value ?? 0));
  const activeIdx = React.useMemo(() => {
    const check = (d: DecileRange) => {
      // Handles inverse measures where from > to by checking min/max
      return clamped >= Math.min(d.from, d.to) && clamped <= Math.max(d.from, d.to);
    };

    // For non-inverse measures (higher is better), we prefer the higher decile on a boundary.
    // We can achieve this by finding the *last* matching index.
    if (!inverse) {
      // findLastIndex is not available in all environments, so we do it manually
      for (let i = deciles.length - 1; i >= 0; i--) {
        if (check(deciles[i])) {
          return i;
        }
      }
    }
    
    // For inverse measures (lower is better), the first match is correct.
    return deciles.findIndex(check);
  }, [deciles, clamped, inverse]);

  if (variant === "muted") {
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="text-base font-semibold text-foreground">Performance Benchmark</h4>
            <p className="text-xs text-muted-foreground">{inverse ? "Lower is better" : "Higher is better"}</p>
          </div>
        </div>

        <div className="relative">
          <div className="flex justify-between items-start gap-1">
            {deciles.map((d, i) => (
              <div key={i} className="flex-1 text-center">
                <div
                  className={cn(
                    "rounded py-1.5 text-xs font-semibold",
                    i === activeIdx ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-600"
                  )}
                >
                  D{i + 1}
                </div>
                <div className="text-[10px] font-medium text-slate-600 mt-1">
                  {d.from.toFixed(1)}-{d.to.toFixed(1)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-blue-800" />
            <span>Current decile</span>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "modal") {
    return (
      <TooltipProvider>
        <div className="w-full space-y-4">
          {/* Top headings like first screenshot */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-foreground">Performance Benchmark</h4>
              <p className="text-xs text-muted-foreground">{inverse ? "Lower is better" : "Higher is better"} • National deciles</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">{formatPct(clamped)}</div>
              <div className="text-xs text-muted-foreground">No data</div>
            </div>
          </div>

          {/* Fatter segmented decile bar */}
          <div className="relative h-8 w-full bg-gray-200 rounded overflow-hidden">
            {(() => {
              const segments = deciles.map((d, i) => {
                const isNotAchievable = d.from === null && d.to === null;
                let width = 10;
                if (d.from !== null && d.to !== null && !isNotAchievable) {
                  width = Math.abs(d.to - d.from);
                } else if (i === 0 && d.to !== null && !isNotAchievable) {
                  width = inverse ? d.to : 100 - d.to;
                } else if (i === 9 && d.from !== null && !isNotAchievable) {
                  width = inverse ? 100 - d.from : d.from;
                }
                let color = '';
                if (isNotAchievable) {
                  color = 'bg-gray-400';
                } else {
                  if (i === 0) color = 'bg-red-600';
                  else if (i === 1) color = 'bg-red-500';
                  else if (i === 2) color = 'bg-red-400';
                  else if (i === 3) color = 'bg-orange-500';
                  else if (i === 4) color = 'bg-orange-400';
                  else if (i === 5) color = 'bg-yellow-500';
                  else if (i === 6) color = 'bg-yellow-400';
                  else if (i === 7) color = 'bg-green-400';
                  else if (i === 8) color = 'bg-green-500';
                  else color = 'bg-green-600';
                }
                return { width, color };
              });
              const totalWidth = segments.reduce((s, seg) => s + seg.width, 0) || 1;
              const performancePos = inverse ? (100 - clamped) : clamped;
              return (
                <>
                  <div className="absolute inset-0 flex">
                    {segments.map((seg, i) => {
                      const percentWidth = (seg.width / totalWidth) * 100;
                      const d = deciles[i];
                      const fmt = (v: number | null) => (v === null ? '--' : `${v.toFixed(2)}%`);
                      return (
                        <Tooltip key={i}>
                          <TooltipTrigger asChild>
                            <div className={`${seg.color} flex items-center justify-center relative`} style={{ width: `${percentWidth}%` }}>
                              {/* Decile numbers inside the bar */}
                              <span className="text-xs font-medium text-white/90 drop-shadow-sm">
                                D{i + 1}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="z-50">
                            <div className="text-xs">D{i + 1}: {fmt(d.from)} – {fmt(d.to)}</div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                  {/* Performance marker - vertical line only */}
                  <div className="absolute inset-y-0 z-30" style={{ left: `${performancePos}%` }}>
                    <div className="relative h-full flex items-center">
                      {/* Prominent vertical line */}
                      <div className="w-1 h-12 bg-blue-800 absolute left-1/2 -translate-x-1/2 -top-2 shadow-sm" />
                      {/* Percentage badge below */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap">
                        <div className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium shadow-md">
                          {formatPct(clamped)}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {algorithm && (
            <div className="text-xs text-blue-600 font-medium text-center">{algorithm}</div>
          )}
        </div>
      </TooltipProvider>
    );
  }

  if (variant === "segmented") {
    return (
      <TooltipProvider>
        <div className="w-full space-y-1">

          {/* Performance Rate and CMS average */}
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-muted-foreground">
              CMS Avg: {cmsAvg !== null && cmsAvg !== undefined ? `${cmsAvg.toFixed(1)}%` : '--'}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-foreground">{formatPct(clamped)}</span>
            </div>
          </div>

          {/* Segmented decile bar with performance marker */}
          <div className="relative h-4 w-full bg-gray-200 rounded overflow-hidden">
            {(() => {
              const segments = deciles.map((d, i) => {
                const isNotAchievable = d.from === null && d.to === null;
                let width = 10;
                if (d.from !== null && d.to !== null && !isNotAchievable) {
                  width = Math.abs(d.to - d.from);
                } else if (i === 0 && d.to !== null && !isNotAchievable) {
                  width = inverse ? d.to : 100 - d.to;
                } else if (i === 9 && d.from !== null && !isNotAchievable) {
                  width = inverse ? 100 - d.from : d.from;
                }
                let color = '';
                if (isNotAchievable) {
                  color = 'bg-gray-400';
                } else {
                  if (i === 0) color = 'bg-red-600';
                  else if (i === 1) color = 'bg-red-500';
                  else if (i === 2) color = 'bg-red-400';
                  else if (i === 3) color = 'bg-orange-500';
                  else if (i === 4) color = 'bg-orange-400';
                  else if (i === 5) color = 'bg-yellow-500';
                  else if (i === 6) color = 'bg-yellow-400';
                  else if (i === 7) color = 'bg-green-400';
                  else if (i === 8) color = 'bg-green-500';
                  else color = 'bg-green-600';
                }
                return { width, color };
              });
              const totalWidth = segments.reduce((s, seg) => s + seg.width, 0) || 1;
              const performancePos = inverse ? (100 - clamped) : clamped;
              return (
                <>
                  <div className="absolute inset-0 flex">
                    {segments.map((seg, i) => {
                      const percentWidth = (seg.width / totalWidth) * 100;
                      const d = deciles[i];
                      const fmt = (v: number | null) => (v === null ? '--' : `${v.toFixed(2)}%`);
                      return (
                        <Tooltip key={i}>
                          <TooltipTrigger asChild>
                            <div className={`${seg.color}`} style={{ width: `${percentWidth}%` }} />
                          </TooltipTrigger>
                          <TooltipContent className="z-50">
                            <div className="text-xs">D{i + 1}: {fmt(d.from)} – {fmt(d.to)}</div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                  {/* Performance marker */}
                  <div className="absolute inset-y-0 z-10" style={{ left: `${performancePos}%` }}>
                    <div className="h-full w-0.5 bg-foreground" />
                    <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2">
                      <span className="text-[10px] font-bold bg-white/95 px-1 rounded shadow-sm">
                        {(() => {
                          // If we found the exact decile, use it
                          if (activeIdx >= 0) {
                            return `D${activeIdx + 1}`;
                          }
                          // Otherwise, estimate based on performance value
                          // This ensures we always show a decile even when decile data is incomplete
                          const estimatedDecile = Math.min(10, Math.max(1, Math.ceil(clamped / 10)));
                          return `D${estimatedDecile}`;
                        })()}
                      </span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{inverse ? "Lower is better" : "Higher is better"}</span>
            {algorithm && <div className="text-xs text-blue-600 font-medium">{algorithm}</div>}
          </div>
        </div>
      </TooltipProvider>
    );
  }

  if (variant === "compact") {
    const getCompactDecileColor = (index: number, isActive: boolean) => {
      const baseColors = [
        "bg-red-700","bg-red-600",
        "bg-amber-700","bg-amber-600","bg-amber-500",
        "bg-amber-500",
        "bg-emerald-700","bg-emerald-600","bg-emerald-600","bg-emerald-500"
      ];
      const colors = inverse ? [...baseColors].reverse() : baseColors;
      if (isActive) return `${colors[index]} ring-2 ring-primary/50 scale-y-125 shadow-md`;
      return `${colors[index]} opacity-70 hover:opacity-85`;
    };

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-full space-y-2">
              <div className="flex items-center gap-4">
                <div className="relative h-4 flex-1 min-w-[120px]">
                <div className="flex h-full rounded-lg overflow-hidden bg-muted/50 border border-border/30">
                  {deciles.map((d, i) => {
                    const isActive = i === activeIdx;
                    return (
                      <div key={i} className={cn("flex-1 transition-all duration-300 relative", getCompactDecileColor(i, isActive))} style={{ transformOrigin: "bottom" }}>
                      </div>
                    );
                  })}
                </div>
                <div className="absolute -bottom-1 transition-all duration-300 z-20" style={{ left: `calc(${clamped}% - 4px)` }}>
                    <div className="w-2 h-6 bg-foreground rounded-sm shadow-lg border border-background">
                    <div className="w-full h-1 bg-primary rounded-t-sm" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center min-w-[60px]">
                  <span className="text-base font-bold text-foreground">{formatPct(clamped)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{inverse ? "Lower is better" : "Higher is better"}</span>
                {algorithm && <div className="text-xs text-blue-600 font-medium">{algorithm}</div>}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent className="z-50 max-w-sm">
            <div className="space-y-2">
              <div className="font-semibold">Performance: {formatPct(clamped)}</div>
              <div className="text-muted-foreground text-xs">{activeIdx >= 0 ? `Decile ${activeIdx + 1} of 10` : "No data"}{inverse && " (inverse measure)"}</div>
              <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t">
                {deciles.map((d, i) => (
                  <div key={i} className={cn("flex items-center gap-1", i === activeIdx && "font-semibold")}> <div className={cn("w-2 h-2 rounded", getCompactDecileColor(i, false).split(' ')[0])} /> <span>D{i + 1}: {formatPct(d.from)}-{formatPct(d.to)}</span></div>
                ))}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Performance Benchmark</h4>
          <p className="text-xs text-muted-foreground">{inverse ? "Lower is better" : "Higher is better"} • National deciles</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-primary">{formatPct(clamped)}</div>
          <div className="text-xs text-muted-foreground">{activeIdx >= 0 ? `Decile ${activeIdx + 1}` : "No data"}</div>
        </div>
      </div>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative pb-12">
              <div className="h-8 w-full rounded-lg bg-muted border border-border/50 overflow-hidden shadow-inner">
                <div className="flex h-full">
                  {deciles.map((d, i) => {
                    const isActive = i === activeIdx;
                    return (
                      <div key={i} className={cn("flex-1 transition-all duration-300 ease-out relative","border-r border-background/20 last:border-r-0", getDecileColor(i, isActive, inverse || false, styleVariant))} style={{ transform: isActive ? "scaleY(1.1)" : "scaleY(1)", transformOrigin: "bottom" }}>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-medium text-white/90 drop-shadow-sm">{i + 1}</span>
                        </div>
                        {isActive && <div className="absolute inset-0 bg-white/20 animate-fade-in rounded-sm" />}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="absolute top-0 transition-all duration-300" style={{ left: `${clamped}%` }}>
                <div className="relative">
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-white px-1.5 py-0.5 rounded text-xs font-bold text-blue-600 border border-blue-200 shadow-sm">
                    {formatPct(clamped)}
                  </div>
                  <div className="w-0.5 h-6 bg-blue-600" />
                  <div className="w-2.5 h-2.5 bg-blue-600 rounded-full border-2 border-white absolute -bottom-1 left-1/2 -translate-x-1/2" />
                </div>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent className="z-50 max-w-sm p-4" side="bottom">
            <div className="space-y-3">
              <div>
                <h5 className="font-semibold text-sm mb-1">Decile Performance Ranges</h5>
                <p className="text-xs text-muted-foreground">{inverse ? "Decile 1 = best performance (lowest %), Decile 10 = worst performance (highest %)" : "Decile 1 = worst performance (lowest %), Decile 10 = best performance (highest %)"}</p>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                {deciles.map((d, i) => (
                  <React.Fragment key={i}>
                    <div className={cn("flex items-center gap-2", i === activeIdx && "font-semibold text-primary")}>
                      <div className={cn("w-3 h-3 rounded bg-gradient-to-r", getDecileColor(i, false, inverse || false, styleVariant).split(' ').slice(1, 3).join(' '))} />
                      <span>D{i + 1}</span>
                    </div>
                    <div className={cn("text-muted-foreground", i === activeIdx && "font-semibold text-primary")}>{formatPct(d.from)} – {formatPct(d.to)}</div>
                  </React.Fragment>
                ))}
              </div>
              {activeIdx >= 0 && (
                <div className="pt-2 border-t border-border">
                  <span className="text-xs font-medium text-primary">Your performance: {formatPct(clamped)} (Decile {activeIdx + 1})</span>
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {activeIdx >= 0 && (
        <div className="flex items-center gap-2 text-xs mt-24">
          <div className={cn("w-2 h-2 rounded-full", activeIdx >= 7 ? "bg-green-500" : activeIdx >= 4 ? "bg-yellow-500" : "bg-red-500")} />
          <span className="text-muted-foreground">{activeIdx >= 7 ? "Excellent performance - top tier" : activeIdx >= 4 ? "Average performance - room for improvement" : "Below average - consider improvement strategies"}</span>
        </div>
      )}
    </div>
  );
}



